import { GetOutput, permissions } from '@jwplayer/companion-gateway';
import { useMemo } from 'react';

import { SessionStore } from '/global-state';
import { useSession } from '/global-state/session/hooks';
import { StatusStore } from '/global-state/status';

import { DebugStore } from '../../debug/store';
import { useSiteContentProtectionSettings } from '../sites/content-protection';
import { useSiteDrmEnabled } from '../sites/drm-enabled';
import { useCurrentSiteIdOrThrow } from '../sites/hooks';

// https://github.com/jwplayer/account-service/blob/develop/src/models/payment_provider.py
const MERCHANT_IDS_WITH_BILLING_PAGE = {
  1: 'recurly',
  5: 'zuora',
  6: 'aws_marketplace',
} as const satisfies Record<number, string>;

type AccountGroupParam = Parameters<typeof permissions.userCan<'account'>>[2];
type SiteGroupParam = Parameters<typeof permissions.userCan<'site'>>[2];

type PermissionsActions = {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

export type Permissions = {
  accountHas: typeof permissions.accountHas;

  accountState: GetOutput<'dashboard-scope', 'read'>['state'];

  isDrmEnabledOnSite: boolean;

  hasSecureEmbeds: boolean;
  hasSecureDownloads: boolean;
  isUrlSigningEnabledOnSite: boolean;

  hasBilling: boolean;
  billingProvider: (typeof MERCHANT_IDS_WITH_BILLING_PAGE)[keyof typeof MERCHANT_IDS_WITH_BILLING_PAGE] | undefined;

  hasTrialExpired: boolean;

  canUpload: boolean;

  isAdsStopped: boolean;
  isStorageStopped: boolean;
  isStreamingStopped: boolean;

  accountPermissions: (group: AccountGroupParam) => PermissionsActions;
  sitePermissions: (group: SiteGroupParam, siteId?: string) => PermissionsActions;
};

function useReadOnlyState() {
  const session = useSession();

  return {
    debug: !!DebugStore.useState((s) => s.readOnly),
    impersonatingUser: !!session.companion.fromAdmin?.readOnly,
    botrApiMaintenance: StatusStore.useState((s) => s.botrStatus?.access_mode === 'read-only'),
  } as const;
}

function getAccountPermissions(
  group: AccountGroupParam,
  readOnly: ReturnType<typeof useReadOnlyState>,
): PermissionsActions {
  return {
    canView: permissions.userCan('view', 'account', group, readOnly),
    canCreate: permissions.userCan('create', 'account', group, readOnly),
    canEdit: permissions.userCan('edit', 'account', group, readOnly),
    canDelete: permissions.userCan('delete', 'account', group, readOnly),
  };
}

function getSitePermissions(
  group: SiteGroupParam,
  siteId: string,
  readOnly: ReturnType<typeof useReadOnlyState>,
): PermissionsActions {
  return {
    canView: permissions.userCan('view', 'site', group, siteId, readOnly),
    canCreate: permissions.userCan('create', 'site', group, siteId, readOnly),
    canEdit: permissions.userCan('edit', 'site', group, siteId, readOnly),
    canDelete: permissions.userCan('delete', 'site', group, siteId, readOnly),
  };
}

export function usePermissions(): Permissions {
  const session = useSession();
  const currentSiteId = useCurrentSiteIdOrThrow();

  const readOnly = useReadOnlyState();

  const accountPermissions = useMemo(
    () =>
      (group: AccountGroupParam): PermissionsActions =>
        getAccountPermissions(group, readOnly),
    [readOnly],
  );

  const sitePermissions = useMemo(
    () =>
      (group: SiteGroupParam, siteId?: string): PermissionsActions =>
        getSitePermissions(group, siteId ?? currentSiteId, readOnly),
    [currentSiteId, readOnly],
  );

  const isDrmEnabledOnSite = useSiteDrmEnabled(currentSiteId);

  const contentProtectionData = useSiteContentProtectionSettings(currentSiteId).query.data;
  const hasSecureEmbeds = contentProtectionData?.allow_embeds === false;
  const hasSecureDownloads = contentProtectionData?.allow_downloads === false;
  const isUrlSigningEnabledOnSite = hasSecureEmbeds || hasSecureDownloads;

  const { isAdsStopped, isStorageStopped, isStreamingStopped } = SessionStore.useState((state) => state.accountLimits);

  const hasBilling: boolean = !!(
    session.payment_provider &&
    session.payment_provider.merchant_id in MERCHANT_IDS_WITH_BILLING_PAGE &&
    session.payment_provider.merchant_ref
  );

  const billingProvider = useMemo(() => {
    const merchantId = session.payment_provider?.merchant_id;
    if (merchantId && merchantId in MERCHANT_IDS_WITH_BILLING_PAGE) {
      return MERCHANT_IDS_WITH_BILLING_PAGE[merchantId as keyof typeof MERCHANT_IDS_WITH_BILLING_PAGE];
    }
  }, [session.payment_provider?.merchant_id]);

  const hasTrialExpired = useMemo(() => {
    if (session.subscription_expiration) {
      return Date.now() > +new Date(session.subscription_expiration);
    }
    return false;
  }, [session.subscription_expiration]);

  const canUpload = useMemo(
    () => permissions.userCan('create', 'site', 'content', currentSiteId, readOnly) && !isStreamingStopped,
    [readOnly, currentSiteId, isStreamingStopped],
  );

  return {
    accountHas: permissions.accountHas,

    accountState: session.state,

    isDrmEnabledOnSite,

    hasSecureEmbeds,
    hasSecureDownloads,
    isUrlSigningEnabledOnSite,

    hasBilling,
    billingProvider,

    hasTrialExpired,

    canUpload,

    isAdsStopped,
    isStorageStopped,
    isStreamingStopped,

    accountPermissions,
    sitePermissions,
  } as const;
}

export function useAccountPermissions(group: Parameters<typeof permissions.userCan<'account'>>[2]): PermissionsActions {
  const readOnly = useReadOnlyState();

  return useMemo(() => getAccountPermissions(group, readOnly), [readOnly]);
}

export function useSitePermissions(
  group: Parameters<typeof permissions.userCan<'site'>>[2],
  siteId?: string,
): PermissionsActions {
  const currentSiteId = useCurrentSiteIdOrThrow();

  const readOnly = useReadOnlyState();

  return useMemo(() => getSitePermissions(group, siteId ?? currentSiteId, readOnly), [currentSiteId, readOnly]);
}
